import { v5 as uuidv5 } from 'uuid';
import { WarehouseTypes, } from './projects';
export var AgentActorSurface;
(function (AgentActorSurface) {
    AgentActorSurface["IN_APP_AGENT"] = "in_app_agent";
    AgentActorSurface["MCP"] = "mcp";
    AgentActorSurface["API"] = "api";
    AgentActorSurface["SLACK_AGENT"] = "slack_agent";
    AgentActorSurface["CLI"] = "cli";
    AgentActorSurface["DATA_APP"] = "data_app";
    AgentActorSurface["AI_SUMMARY"] = "ai_summary";
})(AgentActorSurface || (AgentActorSurface = {}));
export const AGENT_CLIENT_IDS = {
    [AgentActorSurface.IN_APP_AGENT]: 'lightdash-chat',
    [AgentActorSurface.CLI]: 'lightdash-cli',
    [AgentActorSurface.DATA_APP]: 'lightdash-data-app',
    [AgentActorSurface.AI_SUMMARY]: 'lightdash-ai-summary',
    [AgentActorSurface.MCP]: null,
    [AgentActorSurface.API]: null,
    [AgentActorSurface.SLACK_AGENT]: null,
};
// Hand-written: a zod schema here would generate code when the module loads
const parseAgentConnectionGrantRef = (value) => {
    const ref = value;
    if (typeof ref !== 'object' ||
        ref === null ||
        typeof ref.grantUuid !== 'string' ||
        ref.grantUuid.length === 0 ||
        typeof ref.revision !== 'number' ||
        !Number.isInteger(ref.revision) ||
        ref.revision <= 0) {
        throw new Error('Invalid agent connection reference');
    }
    return { grantUuid: ref.grantUuid, revision: ref.revision };
};
export const buildAgentIdentityClaim = ({ subject, surface, clientId, agentUuid = null, agentConnectionGrant = null, }) => ({
    sub: `${subject.type}:${subject.uuid}`,
    subject,
    act: {
        sub: `${surface}:${clientId ?? 'unknown'}`,
        surface,
        client_id: clientId,
        agent_uuid: agentUuid,
        ...(agentConnectionGrant === null
            ? {}
            : {
                agent_connection_grant: parseAgentConnectionGrantRef(agentConnectionGrant),
            }),
    },
});
export const getAgentConnectionGrantRef = (claim) => {
    const reference = claim?.act?.agent_connection_grant;
    return reference == null ? null : parseAgentConnectionGrantRef(reference);
};
export const normalizeAgentIdentityClaim = (claim) => claim === null
    ? null
    : {
        ...claim,
        subject: { ...claim.subject },
        act: {
            ...claim.act,
            agent_uuid: claim.act.agent_uuid ?? null,
            ...(getAgentConnectionGrantRef(claim) === null
                ? {}
                : {
                    agent_connection_grant: getAgentConnectionGrantRef(claim),
                }),
        },
    };
export const getAgentClientLabel = (clientId) => {
    if (clientId === null)
        return 'unknown';
    const lowered = clientId.toLowerCase();
    if (/^[a-z0-9_-]{1,60}$/.test(lowered))
        return lowered;
    return `h-${uuidv5(clientId, uuidv5.URL).replace(/-/g, '')}`;
};
export const AGENT_IDENTITY_SETTINGS_PATH = '/generalSettings/agentIdentity';
export const getProjectAgentIdentitySettingsPath = (projectUuid) => `/generalSettings/projectManagement/${projectUuid}/agentIdentity`;
const markedOnly = {
    person: ['marked_person'],
    service_account: ['marked_person'],
};
export const AGENT_IDENTITY_SOURCES = {
    [WarehouseTypes.SNOWFLAKE]: {
        person: ['marked_person', 'agent_sign_in', 'ai_service_account'],
        service_account: [
            'marked_person',
            'agent_sign_in',
            'ai_service_account',
        ],
    },
    [WarehouseTypes.BIGQUERY]: {
        person: ['marked_person', 'ai_service_account'],
        service_account: ['marked_person', 'ai_service_account'],
    },
    [WarehouseTypes.POSTGRES]: {
        person: ['marked_person', 'ai_service_account'],
        service_account: ['marked_person', 'ai_service_account'],
    },
    [WarehouseTypes.REDSHIFT]: {
        person: ['marked_person', 'ai_service_account'],
        service_account: ['marked_person', 'ai_service_account'],
    },
    [WarehouseTypes.DATABRICKS]: {
        person: ['marked_person', 'ai_service_account'],
        service_account: ['marked_person', 'ai_service_account'],
    },
    [WarehouseTypes.TRINO]: {
        person: ['marked_person', 'ai_service_account'],
        service_account: ['marked_person', 'ai_service_account'],
    },
    [WarehouseTypes.CLICKHOUSE]: {
        person: ['marked_person', 'ai_service_account'],
        service_account: ['marked_person', 'ai_service_account'],
    },
    [WarehouseTypes.ATHENA]: {
        person: ['marked_person', 'ai_service_account'],
        service_account: ['marked_person', 'ai_service_account'],
    },
    [WarehouseTypes.DUCKDB]: markedOnly,
};
export const getAgentIdentityWarehouseTypes = () => Object.keys(AGENT_IDENTITY_SOURCES).filter((type) => Object.values(AGENT_IDENTITY_SOURCES[type]).some((sources) => sources.some((source) => source !== 'marked_person')));
export const isAllowedAgentIdentitySource = (type, source) => Object.values(AGENT_IDENTITY_SOURCES[type]).every((sources) => sources.includes(source));
export const supportsAiServiceAccount = (type) => Object.values(AGENT_IDENTITY_SOURCES[type]).some((sources) => sources.includes('ai_service_account'));
const serviceAuthMethods = {
    [WarehouseTypes.BIGQUERY]: ['private_key'],
    [WarehouseTypes.SNOWFLAKE]: ['password', 'private_key'],
    [WarehouseTypes.POSTGRES]: ['password'],
    [WarehouseTypes.REDSHIFT]: ['password', 'iam'],
    [WarehouseTypes.DATABRICKS]: ['oauth_m2m'],
    [WarehouseTypes.TRINO]: ['password'],
    [WarehouseTypes.CLICKHOUSE]: ['password'],
    [WarehouseTypes.ATHENA]: ['access_key', 'iam_role', 'web_identity'],
    [WarehouseTypes.DUCKDB]: ['token'],
};
export const getWarehouseServiceAuthMethods = (type) => serviceAuthMethods[type];
//# sourceMappingURL=agentIdentity.js.map